/**
 * Partition one readable byte stream into near-equal ranges.
 * Every branch consumes the source once through Web Streams tee().
 */
export default class StreamPartitioner {
  /**
   * @param {ReadableStream<Uint8Array>} source
   * @param {number} size
   * @param {number} count
   * @param {number} [alignment]
   */
  constructor(source, size, count, alignment = 1) {
    this.source = source;
    this.size = size;
    this.count = count;
    this.alignment = alignment;
  }

  /**
   * @returns {{ stream: ReadableStream<Uint8Array>, offset: number, size: number }[]}
   */
  partition() {
    const ranges = [];
    const alignedSize = Math.floor(this.size / this.alignment);
    const baseSize = Math.floor(alignedSize / this.count) * this.alignment;
    let remainder = alignedSize % this.count;
    let offset = 0;
    let source = this.source;

    for (let index = 0; index < this.count; index++) {
      let branch;
      if (index === this.count - 1) {
        branch = source;
      } else {
        [branch, source] = source.tee();
      }

      const rangeSize =
        baseSize +
        (remainder-- > 0 ? this.alignment : 0) +
        (index === this.count - 1
          ? this.size - alignedSize * this.alignment
          : 0);
      ranges.push({
        stream: this.#slice(branch, offset, rangeSize),
        offset,
        size: rangeSize,
      });
      offset += rangeSize;
    }

    return ranges;
  }

  /**
   * @param {ReadableStream<Uint8Array>} source
   * @param {number} start
   * @param {number} size
   */
  #slice(source, start, size) {
    let offset = 0;
    return source.pipeThrough(
      new TransformStream({
        transform(chunk, controller) {
          const end = offset + chunk.byteLength;
          const sliceStart = Math.max(start, offset);
          const sliceEnd = Math.min(start + size, end);
          if (sliceStart < sliceEnd) {
            controller.enqueue(
              chunk.subarray(sliceStart - offset, sliceEnd - offset),
            );
          }
          offset = end;
        },
      }),
    );
  }

  /** @type {number} */
  alignment;
}
